import React, { useState, useEffect, useRef } from 'react';
import {
  FileX, CheckCircle, Lock, Loader2, Upload, AlertCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { OFFBOARDING_STEPS, STEP_STATUS } from '../constants';
import { lifecycleAPI } from '../../../services/api';

/**
 * OffboardingWorkflowPanel — Phase 15.5
 *
 * Replaces the generic onboarding-style WorkflowContainer for properties
 * in offboarding state. Renders each of the 7 offboarding steps with the
 * SPEC-MANDATED dropdown UI:
 *
 *   Step 1 Request Initiated  : completed by Start-Offboarding action (read-only here)
 *   Step 2 GTM Retention      : dropdown [Offboard (+reason) / Cancel Offboarding]
 *   Step 3 ARI Freeze         : dropdown [Completed / Pending]
 *   Step 4 Finance            : dropdown [Commercials checked … / Cancel Offboarding]
 *   Step 5 CX Review          : dropdown [Review Complete]
 *   Step 6 NOC                : dropdown [NOC Complete] + mandatory NOC file upload
 *   Step 7 Final Handover     : dropdown [Do the Offboarding / Cancel Offboarding]
 *
 * RBAC: each step exposes `allowedRoles` (mirror of backend OFFBOARDING_STEPS).
 * If the current user's role is NOT in `allowedRoles`, the step renders as
 * read-only with a Lock icon — the dropdown + action button are hidden.
 *
 * Step locking: a step is locked until the PREVIOUS step is `completed`.
 * Locked steps show "Pending — locked" and never expose actions.
 *
 * Cancellation: server returns offboardingStatus='Cancelled' and resets
 * propertyStatus to 'Active' → caller's onAfterAction reload picks this up
 * and unmounts the OffboardingWorkflowPanel entirely.
 */

const OffboardingStepCard = ({ step, stepData, locked, role, isAdmin, hotelId, focusStep, onAfterAction }) => {
  const [open, setOpen] = useState(stepData?.status !== STEP_STATUS.COMPLETED && !locked);
  const [action, setAction] = useState('');
  const [reason, setReason] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const cardRef = useRef(null);

  const isCompleted = stepData?.status === STEP_STATUS.COMPLETED;

  // Phase 15.6 — Once a step transitions from LOCKED → UNLOCKED (i.e. the
  // previous step just got completed and the parent refetched), force this
  // card OPEN so the user immediately sees the action dropdown without
  // having to click the header. `useState` only runs its initializer on
  // mount, so without this effect a card that was rendered initially as
  // locked would stay collapsed even after the previous step completes.
  useEffect(() => {
    if (!locked && !isCompleted) {
      setOpen(true);
    }
  }, [locked, isCompleted]);

  // Phase 16 — `focusStep` deep-link from notification bell: when the URL
  // says we should land directly on THIS step, force-open + scroll into
  // view. Runs only on first mount + when focusStep changes.
  useEffect(() => {
    if (focusStep && focusStep === step.id) {
      setOpen(true);
      // Defer scroll until after render so layout has settled.
      const t = setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);
      return () => clearTimeout(t);
    }
  }, [focusStep, step.id]);

  const canEdit = !locked && !isCompleted && (isAdmin || (step.allowedRoles || []).includes(role));
  const actions = step.actions || [];
  const selectedAction = actions.find((a) => a.value === action);

  const handleSubmit = async () => {
    if (!action || !selectedAction) {
      setErr('Please choose an action.');
      return;
    }
    if (selectedAction.requiresReason && !reason.trim()) {
      setErr('Reason is required.');
      return;
    }
    if (selectedAction.isNoop) {
      setErr('Step remains pending. Switch to "Completed" to advance.');
      return;
    }

    if (selectedAction.isCancel) {
      const ok = window.confirm(
        'This will CANCEL the entire offboarding workflow, reset all step progress, and restore the property to its onboarded state. Continue?'
      );
      if (!ok) return;
    } else if (selectedAction.isFinal) {
      const ok = window.confirm(
        'This will mark the property as OFFBOARDED. The offboarding workflow will be completed and the property removed from the active portfolio. Continue?'
      );
      if (!ok) return;
    }

    setBusy(true);
    setErr(null);
    try {
      let nocFileUrl = stepData?.noc_file_url;
      // Step 6 — upload NOC file BEFORE submitting the action.
      if (selectedAction.requiresUpload) {
        if (!file && !nocFileUrl) {
          setErr('Please attach the signed NOC file before completing this step.');
          setBusy(false);
          return;
        }
        if (file) {
          setUploading(true);
          const resp = await lifecycleAPI.uploadNocFile(hotelId, file);
          nocFileUrl = resp.data?.noc_file_url;
          setUploading(false);
        }
      }

      await lifecycleAPI.offboardingStepAction(hotelId, step.id, {
        action: selectedAction.value,
        notes: reason || undefined,
        noc_file_url: nocFileUrl,
      });
      // Reset local state then ask parent to reload
      setAction('');
      setReason('');
      setFile(null);
      await onAfterAction?.();
    } catch (e) {
      console.error('[Offboarding step action] failed', e);
      setErr(e?.response?.data?.detail || e?.message || 'Action failed');
    } finally {
      setBusy(false);
      setUploading(false);
    }
  };

  const statusBadge = (() => {
    if (isCompleted) {
      return <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={12} /> Completed</span>;
    }
    if (locked) {
      return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 inline-flex items-center gap-1"><Lock size={12} /> Locked</span>;
    }
    return <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">Pending</span>;
  })();

  return (
    <div
      ref={cardRef}
      data-testid={`offboarding-step-card-${step.id}`}
      className={`border rounded-lg overflow-hidden ${
        isCompleted ? 'border-emerald-200 bg-emerald-50/40'
          : locked ? 'border-gray-200 bg-gray-50'
          : 'border-orange-200 bg-white'
      } ${focusStep === step.id ? 'ring-2 ring-orange-300 ring-offset-2' : ''}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50/60 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
            isCompleted ? 'bg-emerald-500 text-white'
              : locked ? 'bg-gray-300 text-white'
              : 'bg-orange-500 text-white'
          }`}>
            {isCompleted ? <CheckCircle size={16} /> : step.order}
          </div>
          <div className="text-left min-w-0">
            <div className="font-semibold text-gray-900 truncate">{step.name}</div>
            <div className="text-xs text-gray-500 truncate">{step.description}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {statusBadge}
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 space-y-3">
          {/* Completed-state read-only metadata */}
          {isCompleted && (
            <div className="text-xs text-gray-600 space-y-1">
              {stepData.reason && (
                <div><span className="font-medium text-gray-500">Reason:</span> {stepData.reason}</div>
              )}
              {stepData.notes && !stepData.reason && (
                <div><span className="font-medium text-gray-500">Notes:</span> {stepData.notes}</div>
              )}
              {stepData.completedByName && (
                <div><span className="font-medium text-gray-500">By:</span> {stepData.completedByName}{stepData.completedByRole ? ` (${stepData.completedByRole})` : ''}</div>
              )}
              {stepData.completedAt && (
                <div><span className="font-medium text-gray-500">At:</span> {new Date(stepData.completedAt).toLocaleString()}</div>
              )}
              {stepData.noc_file_url && (
                <div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL || ''}${stepData.noc_file_url}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-teal-600 hover:underline"
                    data-testid={`offboarding-step-${step.id}-noc-link`}
                  >
                    <Upload size={12} /> {stepData.noc_file_name || 'View NOC file'}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Step-1 special: shows the initiation reason in expanded view */}
          {!isCompleted && step.id === 'request_initiated' && (
            <div className="text-xs text-gray-500 italic">
              This step is auto-completed when offboarding is started.
            </div>
          )}

          {/* Locked notice */}
          {locked && !isCompleted && (
            <div className="text-xs text-gray-500 inline-flex items-center gap-1">
              <Lock size={12} /> Complete the previous step to unlock this one.
            </div>
          )}

          {/* Role-not-allowed notice */}
          {!locked && !isCompleted && !canEdit && (
            <div className="text-xs text-gray-500 inline-flex items-center gap-1" data-testid={`offboarding-step-${step.id}-rbac-block`}>
              <Lock size={12} /> Only {(step.allowedRoles || []).join(' / ')} can action this step.
              Your role: <span className="font-medium">{role || 'unknown'}</span>.
            </div>
          )}

          {/* Action form */}
          {canEdit && actions.length > 0 && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-700">
                Action
              </label>
              <select
                value={action}
                onChange={(e) => { setAction(e.target.value); setErr(null); }}
                data-testid={`offboarding-step-${step.id}-action-select`}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-orange-500 outline-none"
              >
                <option value="">Choose an action…</option>
                {actions.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>

              {/* Reason text area */}
              {selectedAction?.requiresReason && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {selectedAction.reasonLabel || 'Reason'} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid={`offboarding-step-${step.id}-reason-input`}
                    placeholder="Explain why this property is being offboarded…"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 outline-none"
                    rows={3}
                  />
                </div>
              )}

              {/* NOC file upload */}
              {selectedAction?.requiresUpload && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Signed NOC document <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid={`offboarding-step-${step.id}-noc-file-input`}
                    className="w-full text-xs file:mr-3 file:px-3 file:py-1 file:text-xs file:bg-orange-50 file:text-orange-700 file:border-0 file:rounded"
                  />
                  {file && (
                    <div className="mt-1 text-[11px] text-gray-500">
                      Selected: <span className="font-mono">{file.name}</span> ({Math.round(file.size / 1024)} KB)
                    </div>
                  )}
                </div>
              )}

              {err && (
                <div className="text-xs text-red-600 inline-flex items-start gap-1 bg-red-50 border border-red-200 rounded p-2" data-testid={`offboarding-step-${step.id}-error`}>
                  <AlertCircle size={12} className="shrink-0 mt-0.5" /> {err}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={busy || !action}
                  data-testid={`offboarding-step-${step.id}-submit-btn`}
                  className={`px-3 py-1.5 text-sm rounded font-medium inline-flex items-center gap-2 ${
                    selectedAction?.isCancel ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {(busy || uploading) ? <Loader2 size={14} className="animate-spin" /> : null}
                  {uploading ? 'Uploading…' : busy ? 'Saving…' : 'Save'}
                </button>
                {action && (
                  <button
                    type="button"
                    onClick={() => { setAction(''); setReason(''); setFile(null); setErr(null); }}
                    className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OffboardingWorkflowPanel = ({ hotel, role, isAdmin, focusStep = null, onAfterAction }) => {
  const stepsData = hotel?.offboardingSteps || {};
  const steps = OFFBOARDING_STEPS;
  const total = steps.length;
  const completedCount = steps.filter((s) => stepsData[s.id]?.status === STEP_STATUS.COMPLETED).length;
  const progress = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="offboarding-workflow-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-50 to-white p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-orange-100 flex items-center justify-center">
              <FileX size={28} className="text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Offboarding Workflow</h3>
              <p className="text-sm text-gray-500">Complete all 7 steps to discontinue this property.</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-orange-600" data-testid="offboarding-progress-count">{completedCount} / {total}</div>
            <div className="text-xs text-gray-500" data-testid="offboarding-progress-percent">{progress}% Complete</div>
          </div>
        </div>
        <div className="mt-3 h-2 w-full bg-orange-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${progress === 100 ? 'bg-emerald-500' : 'bg-orange-500'} transition-all`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Steps list */}
      <div className="p-6 space-y-3">
        {steps.map((step, i) => {
          const prev = i > 0 ? stepsData[steps[i - 1].id] : null;
          // Step 1 is always unlocked; subsequent steps require previous = completed.
          const locked = i > 0 && prev?.status !== STEP_STATUS.COMPLETED;
          return (
            <OffboardingStepCard
              key={step.id}
              step={step}
              stepData={stepsData[step.id] || {}}
              locked={locked}
              role={role}
              isAdmin={isAdmin}
              hotelId={hotel.id}
              focusStep={focusStep}
              onAfterAction={onAfterAction}
            />
          );
        })}
      </div>
    </div>
  );
};

export default OffboardingWorkflowPanel;
